import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";

import { isApiError } from "@/api";
import { useMe } from "@/features/auth";
import { useCrew } from "@/features/crew";
import { errorMessage } from "@/i18n/errors";
import { formatList } from "@/shared/lib/format";
import {
  Banner,
  Button,
  Card,
  ChipGroup,
  Icon,
  IconPicker,
  OptionList,
  Screen,
  Segmented,
  Skeleton,
  Stack,
  StepProgress,
  Stepper,
  TextArea,
  TextField,
  Toggle,
  useToast,
} from "@/shared/ui";

import {
  useChallenge,
  useEditChallenge,
  useProposeChallenge,
  type Challenge,
  type ChallengeInput,
} from "../api";
import styles from "../challenges.module.css";
import { ParticipantPicker, pickedFrom } from "../components/ParticipantPicker";
import {
  CHALLENGE_ICONS,
  WEEKDAYS,
  describeDayMin,
  describeLength,
  describeMeasure,
  describePeriod,
  describeProof,
  describePunishments,
  describeRule,
  weekdayShort,
} from "../describe";

/** How often, as the wizard offers it: counted check-ins, or (numbers only) a total. */
const COUNTED = [
  "daily",
  "weekdays",
  "times_per_week",
  "times_per_month",
  "times_per_period",
  "once",
] as const;
const TOTALS = ["total_per_week", "total_per_month", "total_per_period"] as const;
type Often = (typeof COUNTED)[number] | (typeof TOTALS)[number];
const WEEKLY: Often[] = ["times_per_week", "total_per_week"];
/** Per calendar month: offered from 2 months on (one month is the whole period). */
const MONTHLY: Often[] = ["times_per_month", "total_per_month"];
const TIMES: Often[] = ["times_per_week", "times_per_month", "times_per_period"];
/** The most check-ins a week or a month can ask for (February has 28 days); a period: its days. */
const TIMES_MAX: Partial<Record<Often, number>> = { times_per_week: 7, times_per_month: 28 };

/** How long a challenge can run, and the most days each unit has (as the server checks). */
const KINDS = ["month", "week", "day"] as const;
const LENGTH_MAX = { month: 12, week: 52, day: 365 } as const;
const UNIT_DAYS = { month: 31, week: 7, day: 1 } as const;

type Draft = Required<
  Pick<
    ChallengeInput,
    | "title"
    | "rules"
    | "icon"
    | "measure"
    | "unit"
    | "period_kind"
    | "period_length"
    | "proof_required"
  >
> & {
  often: Often;
  on_days: number[];
  times: number;
  /** Numbers as typed ("12,5"), sent with a dot. */
  total: string;
  day_min: string;
  /** null: nobody changed the list yet, so the whole crew takes part. */
  participant_ids: string[] | null;
  /** Who only watches (never one of `participant_ids`). */
  viewer_ids: string[];
  /** `key` only keeps each row's identity while rows are added and removed. */
  punishments: { key: number; text: string; proof_required: boolean }[];
};

const PUNISHMENTS_MAX = 8; // and at least 2, or none (the API checks it too)
let nextPunishmentKey = 0;
const newPunishment = (text = "", proof_required = false) => ({
  key: nextPunishmentKey++,
  text,
  proof_required,
});

const EMPTY: Draft = {
  title: "",
  rules: "",
  icon: "star",
  measure: "check",
  unit: "",
  period_kind: "month",
  period_length: 1,
  often: "daily",
  on_days: [0, 1, 2, 3, 4],
  times: 3,
  total: "",
  day_min: "",
  proof_required: false,
  participant_ids: null,
  viewer_ids: [],
  punishments: [],
};

const STEPS = ["what", "who", "record", "often", "proof", "punishments", "review"] as const;
type Step = (typeof STEPS)[number];

/** Which step shows each field, to send people back to the field the server rejected. */
const FIELD_STEP: Record<string, Step> = {
  title: "what",
  rules: "what",
  icon: "what",
  participant_ids: "who",
  measure: "record",
  unit: "record",
  window: "often",
  on_days: "often",
  need_kind: "often",
  need_value: "often",
  day_min: "often",
  period_kind: "often",
  period_length: "often",
  proof_required: "proof",
  punishments: "punishments",
};

/** A typed number as the API takes it: "12,5" -> "12.5". */
const decimal = (text: string) => text.trim().replace(",", ".");

const isTotal = (often: Often) => (TOTALS as readonly Often[]).includes(often);

type Rule = Pick<ChallengeInput, "window" | "on_days" | "need_kind" | "need_value">;

/** The window and need behind a choice in "How often?". */
function rule(draft: Draft): Rule {
  const counted = (window: Rule["window"], n: number): Rule => ({
    window,
    on_days: [],
    need_kind: "count",
    need_value: String(n),
  });
  const total = (window: Rule["window"]): Rule => ({
    window,
    on_days: [],
    need_kind: "amount",
    need_value: decimal(draft.total),
  });
  switch (draft.often) {
    case "weekdays":
      return { ...counted("day", 1), on_days: draft.on_days };
    case "times_per_week":
      return counted("week", draft.times);
    case "times_per_month":
      return counted("month", draft.times);
    case "times_per_period":
      return counted("period", draft.times);
    case "once":
      return counted("period", 1);
    case "total_per_week":
      return total("week");
    case "total_per_month":
      return total("month");
    case "total_per_period":
      return total("period");
    default:
      return counted("day", 1);
  }
}

/** The choice in "How often?" for a stored challenge. */
function oftenOf(c: Challenge): Often {
  if (c.need_kind === "amount") {
    if (c.window === "week") return "total_per_week";
    return c.window === "month" ? "total_per_month" : "total_per_period";
  }
  if (c.window === "week") return "times_per_week";
  if (c.window === "month") return "times_per_month";
  if (c.window === "period") return c.need_value === 1 ? "once" : "times_per_period";
  return c.on_days.length ? "weekdays" : "daily";
}

function toInput(draft: Draft): ChallengeInput {
  const quantity = draft.measure === "quantity";
  const shape = rule(draft);
  const dayMin = quantity && shape.need_kind === "count" && draft.day_min.trim();
  return {
    title: draft.title,
    rules: draft.rules,
    icon: draft.icon,
    measure: draft.measure,
    unit: quantity ? draft.unit : "",
    ...shape,
    day_min: dayMin ? decimal(draft.day_min) : null,
    period_kind: draft.period_kind,
    period_length: draft.period_length,
    proof_required: draft.proof_required,
    punishments: draft.punishments.map((p) => ({
      text: p.text.trim(),
      proof_required: p.proof_required,
    })),
    ...(draft.participant_ids
      ? { participant_ids: draft.participant_ids, viewer_ids: draft.viewer_ids }
      : {}),
  };
}

/** /challenges/new and /challenges/:id/edit */
export function ProposeRoute() {
  const { id } = useParams();
  return id ? <EditProposal id={id} /> : <ProposeWizard initial={EMPTY} />;
}

function EditProposal({ id }: { id: string }) {
  const { t } = useTranslation();
  const challenge = useChallenge(id);
  if (challenge.isPending) {
    return (
      <Screen withTabBar={false}>
        <Skeleton lines={5} />
      </Screen>
    );
  }
  if (!challenge.data) {
    return (
      <Screen withTabBar={false} title={t("challenges.title")}>
        <Banner tone="danger" title={errorMessage(t, challenge.error)} />
      </Screen>
    );
  }
  const c = challenge.data;
  return (
    <ProposeWizard
      editingId={id}
      initial={{
        title: c.title,
        rules: c.rules,
        icon: c.icon,
        measure: c.measure,
        unit: c.unit,
        period_kind: c.period_kind,
        period_length: c.period_length,
        often: oftenOf(c),
        on_days: c.on_days.length ? c.on_days : EMPTY.on_days,
        times: c.need_kind === "count" && c.window !== "day" ? c.need_value : EMPTY.times,
        total: c.need_kind === "amount" ? String(c.need_value) : "",
        day_min: c.day_min == null ? "" : String(c.day_min),
        proof_required: c.proof_required,
        ...pickedFrom(c.participants),
        punishments: c.punishments.map((p) => newPunishment(p.text, p.proof_required)),
      }}
    />
  );
}

function ProposeWizard({ initial, editingId }: { initial: Draft; editingId?: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const me = useMe();
  const crew = useCrew();
  const propose = useProposeChallenge();
  const edit = useEditChallenge(editingId ?? "");
  const save = editingId ? edit : propose;
  const [draft, setDraft] = useState<Draft>(initial);
  const [step, setStep] = useState<Step>("what");
  const [localErrors, setLocalErrors] = useState<Record<string, string>>({});

  const index = STEPS.indexOf(step);
  const apiError = isApiError(save.error) ? save.error : undefined;
  const fieldError = (name: string) => localErrors[name] ?? apiError?.field(name);
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((current) => ({ ...current, [key]: value }));
  const quantity = draft.measure === "quantity";
  const periodDays = UNIT_DAYS[draft.period_kind] * draft.period_length;
  const monthly = draft.period_kind === "month" && draft.period_length > 1;
  /** Totals need numbers: another kind of record goes back to "every day". */
  const setMeasure = (measure: Draft["measure"]) =>
    setDraft((current) => ({
      ...current,
      measure,
      often: measure !== "quantity" && isTotal(current.often) ? "daily" : current.often,
    }));
  const setPunishment = (key: number, change: Partial<Draft["punishments"][number]>) =>
    set(
      "punishments",
      draft.punishments.map((p) => (p.key === key ? { ...p, ...change } : p)),
    );
  const people = crew.data?.members ?? [];
  const creatorId = me.data?.member?.id; // only the creator proposes or edits

  /** The checks a person needs before moving on; the server checks everything again. */
  const check = (current: Step): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (current === "what" && !draft.title.trim()) errors.title = t("challenges.errors.title");
    if (current === "record" && quantity && !draft.unit.trim())
      errors.unit = t("challenges.errors.unit");
    if (current === "often") {
      if (draft.often === "weekdays" && !draft.on_days.length)
        errors.on_days = t("challenges.errors.onDays");
      if (isTotal(draft.often) && !(Number(decimal(draft.total)) > 0))
        errors.need_value = t("challenges.errors.number");
      if (quantity && draft.day_min.trim() && !(Number(decimal(draft.day_min)) > 0))
        errors.day_min = t("challenges.errors.number");
      if (WEEKLY.includes(draft.often) && periodDays < 7)
        errors.period_length = t("challenges.errors.weekNeedsDays");
      if (MONTHLY.includes(draft.often) && !monthly)
        errors.period_length = t("challenges.errors.monthNeedsMonths");
    }
    if (current === "punishments") {
      const texts = draft.punishments.map((p) => p.text.trim().toLocaleLowerCase());
      if (texts.length === 1) errors.punishments = t("challenges.errors.punishmentsCount");
      else if (texts.some((text) => !text))
        errors.punishments = t("challenges.errors.punishmentText");
      else if (new Set(texts).size < texts.length)
        errors.punishments = t("challenges.errors.punishmentSame");
    }
    return errors;
  };

  const next = (event: FormEvent) => {
    event.preventDefault();
    const errors = check(step);
    setLocalErrors(errors);
    if (Object.keys(errors).length) return;
    if (step !== "review") return setStep(STEPS[index + 1] ?? "review");
    save.mutate(toInput(draft), {
      onSuccess: (challenge) => {
        toast(editingId ? t("challenges.saved") : t("challenges.published"), "success");
        navigate(`/challenges/${challenge.id}`, { replace: true });
      },
      onError: (error) => {
        const field = isApiError(error) ? Object.keys(error.fields ?? {})[0] : undefined;
        if (field && FIELD_STEP[field]) setStep(FIELD_STEP[field]);
      },
    });
  };

  const back = () => (index === 0 ? navigate(-1) : setStep(STEPS[index - 1] ?? "what"));
  const generalError =
    save.error && !Object.keys(apiError?.fields ?? {}).length ? errorMessage(t, save.error) : null;

  return (
    <Screen withTabBar={false} title={t(`challenges.steps.${step}.title`)}>
      <div className={styles.wizardTop}>
        <StepProgress
          current={index + 1}
          total={STEPS.length}
          label={t("challenges.stepLabel", { n: index + 1, total: STEPS.length })}
        />
        <p className={styles.meta}>
          {editingId ? t("challenges.editingNote") : t("challenges.proposingNote")}
        </p>
      </div>
      {generalError && <Banner tone="danger" title={generalError} />}

      <form onSubmit={next} noValidate>
        <Stack gap="lg">
          {step === "what" && (
            <Stack>
              <TextField
                label={t("challenges.fields.title")}
                hint={t("challenges.fields.titleHint")}
                maxLength={60}
                value={draft.title}
                onChange={(e) => set("title", e.target.value)}
                error={fieldError("title")}
              />
              <IconPicker
                label={t("challenges.fields.icon")}
                value={draft.icon}
                onChange={(icon) => set("icon", icon)}
                options={(Object.keys(CHALLENGE_ICONS) as Draft["icon"][]).map((key) => ({
                  value: key,
                  icon: CHALLENGE_ICONS[key],
                  name: t(`challenges.icons.${key}`),
                }))}
              />
              <TextArea
                label={t("challenges.fields.rules")}
                hint={t("challenges.fields.rulesHint")}
                maxLength={500}
                value={draft.rules}
                onChange={(e) => set("rules", e.target.value)}
                error={fieldError("rules")}
              />
            </Stack>
          )}

          {step === "who" &&
            (crew.data ? (
              <ParticipantPicker
                people={people}
                creatorId={creatorId}
                values={{
                  participant_ids: draft.participant_ids ?? people.map((person) => person.id),
                  viewer_ids: draft.viewer_ids,
                }}
                onChange={(picked) => setDraft((d) => ({ ...d, ...picked }))}
                error={fieldError("participant_ids")}
              />
            ) : (
              <Skeleton lines={4} />
            ))}

          {step === "record" && (
            <Stack>
              <OptionList
                label={t("challenges.fields.measure")}
                value={draft.measure}
                onChange={setMeasure}
                options={(["check", "quantity", "abstain"] as const).map((value) => ({
                  value,
                  title: t(`challenges.options.measure.${value}.title`),
                  description: t(`challenges.options.measure.${value}.description`),
                }))}
              />
              {quantity && (
                <TextField
                  label={t("challenges.fields.unit")}
                  hint={t("challenges.fields.unitHint")}
                  maxLength={20}
                  value={draft.unit}
                  onChange={(e) => set("unit", e.target.value)}
                  error={fieldError("unit")}
                />
              )}
            </Stack>
          )}

          {step === "often" && (
            <Stack>
              <Segmented
                label={t("challenges.fields.length")}
                value={draft.period_kind}
                onChange={(kind) =>
                  setDraft((current) => ({
                    ...current,
                    period_kind: kind,
                    period_length: Math.min(current.period_length, LENGTH_MAX[kind]),
                  }))
                }
                options={KINDS.map((value) => ({
                  value,
                  label: t(`challenges.options.periodKind.${value}`),
                }))}
              />
              <Stepper
                label={t(`challenges.fields.periodLength.${draft.period_kind}`)}
                value={draft.period_length}
                min={1}
                max={LENGTH_MAX[draft.period_kind]}
                onChange={(length) => set("period_length", length)}
                decreaseLabel={t("challenges.fields.fewer")}
                increaseLabel={t("challenges.fields.more")}
              />
              {fieldError("period_length") && (
                <Banner tone="danger" title={fieldError("period_length") ?? ""} />
              )}
              <OptionList
                label={t("challenges.fields.often")}
                value={draft.often}
                onChange={(often) => set("often", often)}
                options={(quantity ? [...COUNTED, ...TOTALS] : COUNTED)
                  .filter((value) => periodDays >= 7 || !WEEKLY.includes(value))
                  .filter((value) => monthly || !MONTHLY.includes(value))
                  .map((value) => ({
                    value,
                    title: t(`challenges.options.often.${value}.title`, {
                      period: describePeriod(t, draft),
                    }),
                    description: t(`challenges.options.often.${value}.description`),
                  }))}
              />
              {draft.often === "weekdays" && (
                <ChipGroup
                  label={t("challenges.fields.onDays")}
                  values={draft.on_days}
                  onChange={(days) => set("on_days", [...days].sort())}
                  error={fieldError("on_days")}
                  options={WEEKDAYS.map((day) => ({
                    value: day,
                    label: weekdayShort(t, day),
                    name: t(`challenges.days.long.${day}`),
                  }))}
                />
              )}
              {TIMES.includes(draft.often) && (
                <Stepper
                  label={t("challenges.fields.times")}
                  value={draft.times}
                  min={1}
                  max={TIMES_MAX[draft.often] ?? periodDays}
                  onChange={(times) => set("times", times)}
                  decreaseLabel={t("challenges.fields.fewer")}
                  increaseLabel={t("challenges.fields.more")}
                />
              )}
              {isTotal(draft.often) ? (
                <TextField
                  label={t("challenges.fields.total", { unit: draft.unit })}
                  inputMode="decimal"
                  value={draft.total}
                  onChange={(e) => set("total", e.target.value)}
                  error={fieldError("need_value")}
                />
              ) : (
                quantity && (
                  <TextField
                    label={t("challenges.fields.dayMin", { unit: draft.unit })}
                    hint={t("challenges.fields.dayMinHint")}
                    inputMode="decimal"
                    value={draft.day_min}
                    onChange={(e) => set("day_min", e.target.value)}
                    error={fieldError("day_min")}
                  />
                )
              )}
            </Stack>
          )}

          {step === "proof" && (
            <Toggle
              label={t("challenges.fields.proofRequired")}
              description={t("challenges.fields.proofRequiredHint")}
              checked={draft.proof_required}
              onChange={(on) => set("proof_required", on)}
            />
          )}

          {step === "punishments" && (
            <Stack>
              <p className={styles.muted}>{t("challenges.punishments.intro")}</p>
              {draft.punishments.map((p, i) => (
                <Card key={p.key}>
                  <Stack>
                    <div className={styles.punishmentHead}>
                      <TextField
                        label={t("challenges.punishments.label", { n: i + 1 })}
                        value={p.text}
                        maxLength={80}
                        onChange={(e) => setPunishment(p.key, { text: e.target.value })}
                      />
                      <Button
                        variant="ghost"
                        icon={<Icon name="close" size={20} />}
                        aria-label={t("challenges.punishments.remove", { n: i + 1 })}
                        onClick={() =>
                          set(
                            "punishments",
                            draft.punishments.filter((other) => other.key !== p.key),
                          )
                        }
                      />
                    </div>
                    <Toggle
                      label={t("challenges.punishments.proof")}
                      checked={p.proof_required}
                      onChange={(on) => setPunishment(p.key, { proof_required: on })}
                    />
                  </Stack>
                </Card>
              ))}
              {fieldError("punishments") && (
                <Banner tone="danger" title={fieldError("punishments") ?? ""} />
              )}
              {draft.punishments.length < PUNISHMENTS_MAX && (
                <Button
                  variant="secondary"
                  icon={<Icon name="plus" size={20} />}
                  onClick={() => set("punishments", [...draft.punishments, newPunishment()])}
                >
                  {t("challenges.punishments.add")}
                </Button>
              )}
              <p className={styles.meta}>
                {t("challenges.punishments.count", {
                  n: draft.punishments.length,
                  max: PUNISHMENTS_MAX,
                })}
              </p>
            </Stack>
          )}

          {step === "review" && (
            <Review draft={draft} editing={Boolean(editingId)} people={people} />
          )}

          <div className={styles.wizardNav}>
            <Button variant="secondary" size="lg" onClick={back}>
              {t("challenges.back")}
            </Button>
            <Button type="submit" size="lg" loading={save.isPending}>
              {step === "review"
                ? editingId
                  ? t("challenges.save")
                  : t("challenges.publish")
                : t("challenges.next")}
            </Button>
          </div>
        </Stack>
      </form>
    </Screen>
  );
}

function Review({
  draft,
  editing,
  people,
}: {
  draft: Draft;
  editing: boolean;
  people: { id: string; display_name: string }[];
}) {
  const { t, i18n } = useTranslation();
  const input = toInput(draft);
  const shape = { ...input, on_days: input.on_days ?? [], day_min: input.day_min ?? null };
  const dayMin = describeDayMin(t, shape, i18n.language);
  /** "The whole crew", a few names, or "18 of 20" when many. */
  const whoSummary = (ids: string[] | null, crew: { id: string; display_name: string }[]) => {
    const chosen = crew.filter((person) => !ids || ids.includes(person.id));
    if (chosen.length >= crew.length) return t("challenges.who.everyone");
    if (chosen.length > 5)
      return t("challenges.who.short", { n: chosen.length, total: crew.length });
    return formatList(
      chosen.map((person) => person.display_name),
      i18n.language,
    );
  };
  const watchers = people.filter((person) => draft.viewer_ids.includes(person.id));
  const facts: [string, string][] = [
    [t("challenges.facts.name"), draft.title.trim()],
    [t("challenges.facts.who"), whoSummary(draft.participant_ids, people)],
    ...(watchers.length
      ? [
          [
            t("challenges.facts.watchers"),
            formatList(
              watchers.map((person) => person.display_name),
              i18n.language,
            ),
          ] as [string, string],
        ]
      : []),
    [t("challenges.facts.length"), describeLength(t, draft)],
    [t("challenges.facts.record"), describeMeasure(t, shape)],
    [t("challenges.facts.often"), describeRule(t, shape, i18n.language)],
    ...(dayMin ? [[t("challenges.facts.target"), dayMin] as [string, string]] : []),
    [t("challenges.facts.proof"), describeProof(t, shape)],
    [t("challenges.facts.punishments"), describePunishments(t, draft.punishments.length)],
  ];
  return (
    <Stack>
      <dl className={styles.facts}>
        {facts.map(([label, value]) => (
          <div key={label} className={styles.factRow}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
      {draft.rules.trim() && <p className={styles.muted}>{draft.rules.trim()}</p>}
      <p className={styles.meta}>
        {editing ? t("challenges.editResetsVotes") : t("challenges.reviewNote")}
      </p>
    </Stack>
  );
}
